// Parser + combiner for the two-part location values used by LocationPicker.
// Every pickup / destination value is a single string composed of:
//     "<city display name> <free-typed location detail>"
// e.g. "مكة المكرمة العزيزية شارع الملك عبدالله"
// Special values are self-contained (no detail):
//     "مزارات مكة المكرمة"
//     "مزارات المدينة المنورة"
//     "جولة سياحية في المدينة المنورة"

export const CITY_NAMES = {
  makkah: "مكة المكرمة",
  jeddah: "جدة",
  taif: "الطائف",
  madinah: "المدينة المنورة",
  makkah_train: "محطة قطار مكة المكرمة",
  madinah_train: "محطة قطار المدينة المنورة",
  hotel: "الفندق",
  shrines: "مزارات",
  tour_madinah: "جولة سياحية في المدينة المنورة",
};

// Keyword arrays for parsing. Longest keywords first so multi-word matches
// are tried before shorter overlapping ones.
const CITY_KEYWORDS = {
  tour_madinah: ["جولة سياحية في المدينة المنورة", "جولة سياحية"],
  shrines: ["مزارات المدينة المنورة", "مزارات مكة المكرمة", "المزارات", "مزارات"],
  madinah_train: ["محطة قطار المدينة المنورة", "محطة قطار المدينة"],
  makkah_train: ["محطة قطار مكة المكرمة", "محطة قطار مكة"],
  hotel: ["الفندق", "فندق"],
  makkah: ["مكة المكرمة", "مكة", "مكه"],
  madinah: ["المدينة المنورة", "المدينة", "المدينه"],
  jeddah: ["جدة", "جده"],
  taif: ["الطائف", "الطايف", "طائف"],
};

// Try in this order (longest / most-specific first).
const PARSE_ORDER = [
  "tour_madinah",
  "shrines",
  "madinah_train",
  "makkah_train",
  "hotel",
  "makkah",
  "madinah",
  "jeddah",
  "taif",
];

// Sub-options shown when city === "shrines".
export const SHRINE_SUBOPTIONS = [
  { key: "shrine_makkah", label: "مزارات مكة المكرمة", city_for_lookup: "مزارات مكة المكرمة" },
  { key: "shrine_madinah", label: "مزارات المدينة المنورة", city_for_lookup: "مزارات المدينة المنورة" },
];

export function parseValue(v) {
  const t = (v || "").trim();
  if (!t) return { city: null, location: "" };
  for (const key of PARSE_ORDER) {
    for (const kw of CITY_KEYWORDS[key]) {
      if (t.startsWith(kw)) {
        // Shrines are self-contained: the matched keyword IS the full label
        // (e.g. "مزارات مكة المكرمة"). Preserve it in `location` so the
        // LocationPicker's state can round-trip without losing the detail.
        if (key === "shrines") {
          return { city: "shrines", location: kw };
        }
        return { city: key, location: t.slice(kw.length).trim() };
      }
    }
  }
  return { city: null, location: t };
}

export function combine(city, location) {
  const name = city ? CITY_NAMES[city] : "";
  const loc = (location || "").trim();
  if (name && loc) return `${name} ${loc}`;
  return name || loc;
}

// Return the value used for backend /route-prices lookup.
// Route rules match on the CITY portion (e.g., "مكة المكرمة").
// Special cases: shrines / tour / train / hotel return their fixed display name so
// the backend alias table can map them to the appropriate parent city rules.
export function cityLookupValue(combined) {
  const t = (combined || "").trim();
  if (!t) return "";
  // Shrine sub-options are stored as-is and match rules like
  // pickup_pattern = "مزارات مكة المكرمة".
  for (const opt of SHRINE_SUBOPTIONS) {
    if (t.startsWith(opt.city_for_lookup)) return opt.city_for_lookup;
  }
  // "جولة سياحية في المدينة المنورة" is self-contained.
  if (t.startsWith("جولة سياحية")) return "جولة سياحية في المدينة المنورة";
  const parsed = parseValue(t);
  if (parsed.city) return CITY_NAMES[parsed.city];
  return t;
}
